/**
 * Competitor Intelligence Service
 *
 * Handles API calls for Link Policy (Never Use / Do Use lists),
 * SERP analysis, and URL comparison.
 */

import { apiClient } from "./apiClient";

export interface LinkPolicyEntry {
	id: string;
	website: string;
	domain: string;
	name?: string;
	policy_type: "never_use" | "do_use";
	created_at?: string;
	affected_content?: {
		count: number;
		articles: Array<{ id: string; keyword: string; wordpress_url: string }>;
	};
}

export interface LinkPolicyListResponse {
	entries: LinkPolicyEntry[];
}

export interface AddToPolicyRequest {
	tenant_id: string;
	domain: string;
	policy_type: "never_use" | "do_use";
	name?: string;
}

export interface SERPAnalysisResult {
	keyword: string;
	average_word_count: number;
	recommended_word_count: number;
	word_count_range: { min: number; max: number } | null;
	h2_patterns: string[];
	common_topics: string[];
	competitors: Array<{
		url: string;
		title: string;
		word_count: number;
		h2_headings: string[];
		main_topics: string[];
	}>;
	from_cache: boolean;
	analyzed_at: string;
	error_message?: string;
	warning?: string;
	analysis_duration_ms?: number;
}

export const competitorService = {
	// ------------------------------------------------------------------
	// Link Policy
	// ------------------------------------------------------------------

	async getLinkPolicy(
		tenantId: string,
		policyType?: "never_use" | "do_use",
	): Promise<LinkPolicyListResponse> {
		const params: Record<string, string> = { tenant_id: tenantId };
		if (policyType) params.policy_type = policyType;

		const response = await apiClient.get<LinkPolicyListResponse>(
			"/seo-tools/link-policy",
			{ params },
		);
		return response.data;
	},

	async addToPolicy(request: AddToPolicyRequest): Promise<LinkPolicyEntry> {
		const response = await apiClient.post<LinkPolicyEntry>(
			"/seo-tools/link-policy",
			request,
		);
		return response.data;
	},

	async removeFromPolicy(
		entryId: string,
		tenantId: string,
	): Promise<void> {
		await apiClient.delete(`/seo-tools/link-policy/${entryId}`, {
			params: { tenant_id: tenantId },
		});
	},

	// ------------------------------------------------------------------
	// SERP Analysis
	// ------------------------------------------------------------------

	async analyzeSERP(
		keyword: string,
		tenantId: string,
	): Promise<SERPAnalysisResult> {
		const response = await apiClient.post<SERPAnalysisResult>(
			"/content/serp-analyze",
			{ keyword, tenant_id: tenantId },
		);
		return response.data;
	},

	// ------------------------------------------------------------------
	// URL Comparison
	// ------------------------------------------------------------------

	async compareUrls(urls: string[]): Promise<unknown> {
		const response = await apiClient.post("/seo-tools/compare", { urls });
		return response.data;
	},
};
